import { createClient } from "./client";
import type {
  StoredListing,
  GeneratedListing,
  SerializedImage,
  ListingStatus,
  AiOriginal,
} from "@/lib/types";

// ── Profile & Stats queries ────────────────────────────────────────────

export interface UserProfile {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  email: string | null;
  bio: string | null;
  platform_preferences: string[] | null;
  selling_experience: string | null;
  monthly_volume: string | null;
  item_categories: string[] | null;
  selling_motivation: string | null;
  onboarding_completed: boolean;
}

export interface ListingDateEntry {
  created_at: string;
  prix_min: number;
  prix_max: number;
  status: ListingStatus;
}

export interface UserStats {
  totalListings: number;
  totalValue: number;
  avgPrice: number;
  countByStatus: Record<ListingStatus, number>;
  categories: string[];
  listingDates: ListingDateEntry[];
}

export interface MonthlyCreation {
  month: string;
  count: number;
}

/**
 * Fetch the current user's profile (display_name, avatar_url, email, bio, platform_preferences).
 */
export async function fetchUserProfile(): Promise<UserProfile | null> {
  const supabase = createClient();
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();
  if (authError || !user) return null;

  const { data: profile } = await supabase
    .from("profiles")
    .select(
      "id, display_name, avatar_url, bio, platform_preferences, selling_experience, monthly_volume, item_categories, selling_motivation, onboarding_completed"
    )
    .eq("id", user.id)
    .single();

  return {
    id: user.id,
    display_name: profile?.display_name ?? null,
    avatar_url: profile?.avatar_url ?? null,
    email: user.email ?? null,
    bio: profile?.bio ?? null,
    platform_preferences: profile?.platform_preferences ?? null,
    selling_experience: profile?.selling_experience ?? null,
    monthly_volume: profile?.monthly_volume ?? null,
    item_categories: profile?.item_categories ?? null,
    selling_motivation: profile?.selling_motivation ?? null,
    onboarding_completed: profile?.onboarding_completed ?? false,
  };
}

/**
 * Fetch aggregate stats for the current user: total listings, total value, avg price, count by status.
 */
export async function fetchUserStats(): Promise<UserStats> {
  const empty: UserStats = {
    totalListings: 0,
    totalValue: 0,
    avgPrice: 0,
    countByStatus: { active: 0, exported: 0, published: 0, sold: 0, archived: 0 },
    categories: [],
    listingDates: [],
  };
  const supabase = createClient();
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return empty;

  const { data: listings, error } = await supabase
    .from("listings")
    .select("created_at, prix_min, prix_max, status, categorie")
    .eq("user_id", user.id)
    .is("deleted_at", null);

  if (error || !listings || listings.length === 0) return empty;

  let totalValue = 0;
  const countByStatus: Record<ListingStatus, number> = { active: 0, exported: 0, published: 0, sold: 0, archived: 0 };
  const categories: string[] = [];

  for (const l of listings) {
    const avg = (Number(l.prix_min) + Number(l.prix_max)) / 2;
    totalValue += avg;
    const status = (l.status as ListingStatus) ?? "active";
    countByStatus[status] = (countByStatus[status] ?? 0) + 1;
    if (l.categorie) categories.push(l.categorie as string);
  }

  return {
    totalListings: listings.length,
    totalValue: Math.round(totalValue),
    avgPrice: Math.round(totalValue / listings.length),
    countByStatus,
    categories,
    listingDates: listings.map((l) => ({
      created_at: l.created_at,
      prix_min: Number(l.prix_min),
      prix_max: Number(l.prix_max),
      status: (l.status as ListingStatus) ?? "active",
    })),
  };
}

/**
 * Fetch monthly listing creation counts for the last 6 months.
 */
export async function fetchMonthlyCreations(): Promise<MonthlyCreation[]> {
  const supabase = createClient();
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return [];

  // Calculate 6 months ago from today
  const now = new Date();
  const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1);

  const { data: listings, error } = await supabase
    .from("listings")
    .select("created_at")
    .eq("user_id", user.id)
    .is("deleted_at", null)
    .gte("created_at", sixMonthsAgo.toISOString())
    .order("created_at", { ascending: true });

  if (error || !listings) return [];

  // Build month buckets
  const months: MonthlyCreation[] = [];
  const monthNames = [
    "Jan",
    "Fév",
    "Mar",
    "Avr",
    "Mai",
    "Juin",
    "Juil",
    "Août",
    "Sep",
    "Oct",
    "Nov",
    "Déc",
  ];

  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
    months.push({ month: monthNames[d.getMonth()], count: 0 });
  }

  // Count listings per month
  for (const l of listings) {
    const d = new Date(l.created_at);
    const monthOffset =
      (d.getFullYear() - sixMonthsAgo.getFullYear()) * 12 +
      (d.getMonth() - sixMonthsAgo.getMonth());
    if (monthOffset >= 0 && monthOffset < 6) {
      months[monthOffset].count++;
    }
  }

  return months;
}

/**
 * Update the current user's profile fields.
 */
export async function updateUserProfile(fields: {
  display_name?: string | null;
  bio?: string | null;
  avatar_url?: string | null;
  platform_preferences?: string[] | null;
  selling_experience?: string | null;
  monthly_volume?: string | null;
  item_categories?: string[] | null;
  selling_motivation?: string | null;
  onboarding_completed?: boolean;
}): Promise<boolean> {
  const supabase = createClient();
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return false;

  const { error } = await supabase.from("profiles").upsert({
    id: user.id,
    ...fields,
    updated_at: new Date().toISOString(),
  });

  return !error;
}

/**
 * Fetch all listings for the current user, most recent first.
 */
export async function fetchListings(): Promise<StoredListing[]> {
  const supabase = createClient();
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();
  if (authError) console.error("[queries] Auth error:", authError);
  if (!user) return [];

  const { data: listings, error } = await supabase
    .from("listings")
    .select("*, listing_images(*)")
    .eq("user_id", user.id)
    .is("deleted_at", null)
    .order("created_at", { ascending: false });

  if (error || !listings) return [];

  return listings.map((l) => dbToStoredListing(l, supabase));
}

/**
 * Fetch a single listing by ID.
 */
export async function fetchListingById(id: string): Promise<StoredListing | null> {
  const supabase = createClient();

  const { data: listing, error } = await supabase
    .from("listings")
    .select("*, listing_images(*)")
    .eq("id", id)
    .is("deleted_at", null)
    .single();

  if (error || !listing) return null;

  return dbToStoredListing(listing, supabase);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function dbToStoredListing(row: any, supabase: any): StoredListing {
  const text: GeneratedListing = {
    titre: row.titre,
    description: row.description,
    hashtags: row.hashtags,
    prix_suggere: { min: Number(row.prix_min), max: Number(row.prix_max) },
    prix_utilisateur: row.prix_utilisateur != null ? Number(row.prix_utilisateur) : undefined,
    categorie: row.categorie,
  };

  const images: SerializedImage[] = (row.listing_images ?? []).map(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (img: any) => {
      const { data } = supabase.storage.from("generated").getPublicUrl(img.storage_path);
      return {
        variant: img.variant,
        width: img.width ?? 1080,
        height: img.height ?? 1440,
        dataUrl: data.publicUrl,
      };
    }
  );

  // Original image URL
  let originalImageDataUrl = "";
  if (row.original_image_path) {
    const { data } = supabase.storage.from("originals").getPublicUrl(row.original_image_path);
    originalImageDataUrl = data.publicUrl;
  }

  const aiOriginal: AiOriginal | undefined = row.ai_original
    ? {
        titre: row.ai_original.titre,
        description: row.ai_original.description,
        hashtags: row.ai_original.hashtags,
        prix_min: Number(row.ai_original.prix_min),
        prix_max: Number(row.ai_original.prix_max),
        categorie: row.ai_original.categorie,
      }
    : undefined;

  return {
    id: row.id,
    date: row.created_at,
    originalImageDataUrl,
    text,
    images,
    status: (row.status as ListingStatus) ?? "active",
    generationStatus: row.generation_status ?? "complete",
    aiOriginal,
  };
}
